export interface ParsedButton {
  type?: string;
  text: string;
  url?: string;
  phone_number?: string;
}

export interface ParsedComponent {
  type: string;
  format?: string;
  text?: string;
  buttons?: ParsedButton[];
}

/**
 * Safe parse of the `components` JSONB blob. Returns `[]` if the payload is
 * malformed so views can render an empty preview without crashing.
 */
export function parseComponents(raw: string | null | undefined): ParsedComponent[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.map((c): ParsedComponent => ({
      type: String((c as { type?: unknown })?.type ?? 'UNKNOWN').toUpperCase(),
      format: typeof (c as { format?: unknown })?.format === 'string' ? (c as { format: string }).format : undefined,
      text: typeof (c as { text?: unknown })?.text === 'string' ? (c as { text: string }).text : undefined,
      buttons: Array.isArray((c as { buttons?: unknown })?.buttons)
        ? ((c as { buttons: unknown[] }).buttons.map((b) => ({
          type: typeof (b as { type?: unknown })?.type === 'string' ? (b as { type: string }).type : undefined,
          text: typeof (b as { text?: unknown })?.text === 'string' ? (b as { text: string }).text : '',
          url: typeof (b as { url?: unknown })?.url === 'string' ? (b as { url: string }).url : undefined,
          phone_number: typeof (b as { phone_number?: unknown })?.phone_number === 'string'
            ? (b as { phone_number: string }).phone_number
            : undefined
        })))
        : undefined
    }));
  } catch {
    return [];
  }
}

/**
 * Extracts the unique variable indices ({{1}}, {{2}}, …) from every text
 * field across all components. Returned sorted ascending.
 */
export function extractVariables(components: ParsedComponent[]): number[] {
  const found = new Set<number>();
  for (const c of components) {
    if (c.text) {
      for (const match of c.text.matchAll(/\{\{\s*(\d+)\s*\}\}/g)) {
        const n = Number(match[1]);
        if (Number.isFinite(n)) found.add(n);
      }
    }
    if (c.buttons) {
      for (const b of c.buttons) {
        if (b.text) {
          for (const match of b.text.matchAll(/\{\{\s*(\d+)\s*\}\}/g)) {
            const n = Number(match[1]);
            if (Number.isFinite(n)) found.add(n);
          }
        }
      }
    }
  }
  return Array.from(found).sort((a, b) => a - b);
}
